import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

const EFFECTIVE_DATE = "February 2026";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-16 tga-fade" data-testid="privacy-page">
      <button
        data-testid="privacy-back-btn"
        onClick={() => navigate("/")}
        className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-1 mb-6"
      >
        <ChevronLeft size={16} /> Back
      </button>

      <div className="text-center mb-8">
        <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-img mx-auto mb-4" />
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brass-bright)]">
          Privacy Policy
        </h1>
        <p className="mt-2 text-[color:var(--muted-green)] italic font-display">
          Effective {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="tga-card space-y-6 text-[15px] leading-relaxed">
        <section>
          <p>
            This Privacy Policy describes how <strong>TGA Golf</strong> ("we", "us", or "our")
            collects, uses, and shares information when you use our golf scoring web
            application (the "Service"). By using the Service you agree to the collection
            and use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h2 className="section-title">1. Information We Collect</h2>
          <p className="mb-2"><strong>Account information.</strong> When you sign in with Google, we receive your name, email address, profile picture, and a unique Google account identifier.</p>
          <p className="mb-2"><strong>Trip and scoring data.</strong> Trip names, player names, hole-by-hole scores, side-game selections and payouts, courses, and any notes you add are stored so trips can sync across devices.</p>
          <p className="mb-2"><strong>Technical data.</strong> Basic request metadata (IP address, browser, timestamps) may be recorded in server logs for security and reliability.</p>
          <p><strong>Cookies and local storage.</strong> We use cookies and browser storage to keep you signed in and to remember trip preferences.</p>
        </section>

        <section>
          <h2 className="section-title">2. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>To operate the scoring, leaderboard, side-game, and season-standings features.</li>
            <li>To sync your trips across your devices and other players in your group.</li>
            <li>To enforce organizer roles and prevent unauthorized edits.</li>
            <li>To debug issues and protect the Service from abuse.</li>
          </ul>
        </section>

        <section>
          <h2 className="section-title">3. How Information Is Shared</h2>
          <p className="mb-2"><strong>With other players.</strong> Names, scores, and side-game results in a trip are visible to everyone who joins that trip's code.</p>
          <p className="mb-2"><strong>Service providers.</strong> We use Google (authentication), MongoDB (data storage), and GolfCourseAPI (course lookups). Course search queries you make are sent to GolfCourseAPI to return results.</p>
          <p>We do not sell your personal information.</p>
        </section>

        <section>
          <h2 className="section-title">4. Data Retention</h2>
          <p>
            Trip data is retained for as long as the trip exists. You may ask us to
            delete your account and associated trips you own at any time by emailing
            the address below.
          </p>
        </section>

        <section>
          <h2 className="section-title">5. Security</h2>
          <p>
            We use industry-standard measures (HTTPS, access controls, hashed session
            tokens) to protect your data. No online service is 100% secure, and we
            cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="section-title">6. Children</h2>
          <p>
            The Service is not directed to children under 13, and we do not knowingly
            collect personal information from them.
          </p>
        </section>

        <section>
          <h2 className="section-title">7. Your Rights</h2>
          <p>
            Depending on where you live, you may have rights to access, correct, export,
            or delete your personal information. Contact us to exercise these rights.
          </p>
        </section>

        <section>
          <h2 className="section-title">8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will
            be reflected by updating the effective date at the top of this page.
          </p>
        </section>

        <section>
          <h2 className="section-title">9. Contact</h2>
          <p>
            Questions or requests? Email{" "}
            <a
              className="underline text-[color:var(--brass-dark)]"
              href="mailto:privacy@tgagolf.com"
              data-testid="privacy-contact-email"
            >
              privacy@tgagolf.com
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
